import React, { useState, useEffect } from "react";
import {
  Typography,
  Space,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Tabs,
  message,
  Alert,
  Spin,
  Divider,
  Row,
  Col,
  Tag,
  Tooltip,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  BankOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
  SettingOutlined,
  LockOutlined,
  UnlockOutlined,
  HeartOutlined,
  TeamOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  getAllSettings,
  setMultipleSettings,
} from "../features/systemSettings/services/systemSettingsService.js";
import UnsafeAreasSettingsPanel from "../features/systemSettings/components/UnsafeAreasSettingsPanel.jsx";
import PricingSettingsPanel from "../features/systemSettings/components/PricingSettingsPanel.jsx";
import MarketplaceSettingsPanel from "../features/systemSettings/components/MarketplaceSettingsPanel.jsx";
import LeadIntelligenceSettingsPanel from "../features/systemSettings/components/LeadIntelligenceSettingsPanel.jsx";
import QuotePolicySettingsPanel from "../features/systemSettings/components/QuotePolicySettingsPanel.jsx";
import {
  PRICING_SETTING_KEYS,
  DEMOTED_PRICING_SETTING_KEYS,
  RETIRED_PRICING_SETTING_KEYS,
  getPricingFieldMeta,
} from "../features/systemSettings/utils/pricingSettingsMeta.js";
import {
  MARKETPLACE_SETTING_KEYS,
  getMarketplaceFieldMeta,
  getMissingMarketplaceSettings,
  isMarketplaceSettingKey,
} from "../features/systemSettings/utils/marketplaceSettingsMeta.js";
import {
  LEAD_INTELLIGENCE_SETTING_KEYS,
  getLeadIntelligenceFieldMeta,
  getMissingLeadIntelligenceSettings,
  isLeadIntelligenceSettingKey,
} from "../features/systemSettings/utils/leadIntelligenceSettingsMeta.js";

const { Title, Text } = Typography;
const { TextArea } = Input;

const SettingsContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionCard = styled(Card)`
  margin-bottom: 24px;
`;

const SettingRow = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const SettingLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SettingDescription = styled(Text)`
  display: block;
  margin-top: 4px;
  color: #8c8c8c;
  font-size: 12px;
`;

const MarketplacePanelBlock = styled.div`
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const LeadIntelligencePanelBlock = styled.div`
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const QuotePolicyPanelBlock = styled.div`
  margin-bottom: 8px;
`;

const SETTING_LABELS = {
  "leads.fulfilmentDiscovery.notificationEmail":
    "Fulfilment Discovery Notification Email",
  "leads.fulfilmentDiscovery.emailNotificationsEnabled":
    "Fulfilment Discovery Email Notifications",
  "ops.safety.unsafeAreas": "Unsafe Areas (SA)",
  "marketplace.providerApply.googleFormUrl":
    "Provider apply Google Form URL",
  "marketplace.providerApply.googleFormPrefill":
    "Provider apply Google Form prefill map",
  "marketplace.providerOnboarding.enabled":
    "Provider onboarding gate enabled",
};

const formatSettingLabel = (key) => {
  if (SETTING_LABELS[key]) return SETTING_LABELS[key];
  const marketplaceMeta = getMarketplaceFieldMeta(key);
  if (marketplaceMeta?.label) return marketplaceMeta.label;
  const leadIntelMeta = getLeadIntelligenceFieldMeta(key);
  if (leadIntelMeta?.label) return leadIntelMeta.label;
  return key
    .split(".")
    .pop()
    .replace(/([A-Z])/g, " $1")
    .trim();
};

const SETTINGS_TAB_KEYS = new Set([
  "business",
  "contact",
  "payments",
  "pricing",
  "quotes",
  "leads",
  "tokens",
  "limits",
  "features",
  "email",
]);

const hasSettingValueChanged = (previousValue, nextValue) => {
  if (previousValue === nextValue) return false;
  if (
    typeof previousValue === "object" ||
    typeof nextValue === "object"
  ) {
    return JSON.stringify(previousValue) !== JSON.stringify(nextValue);
  }
  return true;
};

const normalizeSettingValue = (value, type) => {
  if (type === "string" && value != null && typeof value !== "string") {
    return String(value);
  }
  if (type === "array" || type === "object") {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) {
        return type === "array" ? [] : {};
      }
      return JSON.parse(trimmed);
    }
    return value;
  }
  return value;
};

const toFormValue = (value, type, key) => {
  // Custom editors keep structured values (not JSON strings)
  if (key === "ops.safety.unsafeAreas") {
    return Array.isArray(value) ? value : [];
  }
  if (type === "array" || type === "object") {
    return JSON.stringify(value ?? (type === "array" ? [] : {}), null, 2);
  }
  return value;
};

const Settings = () => {
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState([]);
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    SETTINGS_TAB_KEYS.has(tabFromUrl) ? tabFromUrl : "business"
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Group settings by category
  const settingsByCategory = React.useMemo(() => {
    const grouped = {};
    settings.forEach((setting) => {
      if (RETIRED_PRICING_SETTING_KEYS.includes(setting.key)) {
        return;
      }
      if (DEMOTED_PRICING_SETTING_KEYS.includes(setting.key)) {
        return;
      }
      const category = setting.key.startsWith("pricing.")
        ? "pricing"
        : setting.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(setting);
    });
    return grouped;
  }, [settings]);

  const marketplaceSettings = React.useMemo(
    () => settings.filter((setting) => isMarketplaceSettingKey(setting.key)),
    [settings]
  );

  const leadIntelligenceSettings = React.useMemo(
    () =>
      settings.filter((setting) => isLeadIntelligenceSettingKey(setting.key)),
    [settings]
  );

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const nextTab = searchParams.get("tab");
    if (SETTINGS_TAB_KEYS.has(nextTab) && nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getAllSettings();
      if (response.success) {
        const fromApi = response.data || [];
        // Inject marketplace + lead-intelligence keys missing from legacy DBs.
        const missingMarketplace = getMissingMarketplaceSettings(fromApi);
        const missingLeadIntelligence =
          getMissingLeadIntelligenceSettings(fromApi);
        const mergedSettings = [
          ...missingMarketplace,
          ...missingLeadIntelligence,
          ...fromApi,
        ];
        setSettings(mergedSettings);
        // Set form values
        const formValues = {};
        mergedSettings.forEach((setting) => {
          formValues[setting.key] = toFormValue(
            setting.value,
            setting.type,
            setting.key
          );
        });
        // Ensure pricing form fields exist even if DB rows are missing (legacy DBs).
        PRICING_SETTING_KEYS.forEach((key) => {
          if (!(key in formValues)) {
            const meta =
              key === "pricing.economics.providerCut.enabled"
                ? true
                : key.endsWith(".enabled") || key.endsWith(".vatApplicable")
                ? false
                : key.endsWith(".bands") || key.endsWith(".catalogue")
                ? "[]"
                : key.includes("Amount") || key.includes("Factor") || key.includes("rate")
                  ? 0
                  : undefined;
            if (meta !== undefined) {
              formValues[key] = meta;
            }
          }
        });
        MARKETPLACE_SETTING_KEYS.forEach((key) => {
          if (!(key in formValues)) {
            const meta = getMarketplaceFieldMeta(key);
            formValues[key] = meta?.defaultValue;
          }
        });
        LEAD_INTELLIGENCE_SETTING_KEYS.forEach((key) => {
          if (!(key in formValues)) {
            const meta = getLeadIntelligenceFieldMeta(key);
            formValues[key] = meta?.defaultValue;
          }
        });
        form.setFieldsValue(formValues);
        setHasChanges(false);
      } else {
        message.error(response.error || "Failed to load settings");
      }
    } catch (error) {
      message.error("Failed to load settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = form.getFieldsValue(true);

      // Only compare fields registered in the form (mounted tab panes).
      // Unmounted tabs return undefined and must not be treated as changes.
      const changedSettings = [];
      settings.forEach((setting) => {
        if (RETIRED_PRICING_SETTING_KEYS.includes(setting.key)) {
          return;
        }
        if (DEMOTED_PRICING_SETTING_KEYS.includes(setting.key)) {
          return;
        }
        if (!(setting.key in values)) {
          return;
        }

        let newValue;
        try {
          newValue = normalizeSettingValue(
            values[setting.key],
            setting.type
          );
        } catch (parseError) {
          throw new Error(
            `Invalid JSON for ${setting.key}: ${parseError.message}`
          );
        }

        if (!hasSettingValueChanged(setting.value, newValue)) {
          return;
        }

        changedSettings.push({
          key: setting.key,
          value: newValue,
          type: setting.type,
          scope: setting.scope,
          category: setting.key.startsWith("pricing.")
            ? "pricing"
            : setting.category,
          description: setting.description,
          metadata: setting.metadata,
        });
      });

      const settingsKeySet = new Set(settings.map((setting) => setting.key));
      PRICING_SETTING_KEYS.forEach((key) => {
        if (settingsKeySet.has(key) || !(key in values)) {
          return;
        }

        const meta = getPricingFieldMeta(key);
        let newValue;
        try {
          newValue = normalizeSettingValue(values[key], meta.type);
        } catch (parseError) {
          throw new Error(
            `Invalid JSON for ${key}: ${parseError.message}`
          );
        }

        if (meta.type === "number" && (newValue === 0 || newValue == null)) {
          return;
        }
        if (meta.type === "boolean" && newValue !== true) {
          return;
        }
        if (meta.type === "string" && !newValue) {
          return;
        }
        if (
          meta.type === "array" &&
          (!Array.isArray(newValue) || newValue.length === 0)
        ) {
          return;
        }

        changedSettings.push({
          key,
          value: newValue,
          type: meta.type,
          scope: "private",
          category: "pricing",
          description: meta.description,
          metadata: meta.metadata,
        });
      });

      LEAD_INTELLIGENCE_SETTING_KEYS.forEach((key) => {
        if (settingsKeySet.has(key) || !(key in values)) {
          return;
        }

        const meta = getLeadIntelligenceFieldMeta(key);
        let newValue;
        try {
          newValue = normalizeSettingValue(values[key], meta.type);
        } catch (parseError) {
          throw new Error(
            `Invalid JSON for ${key}: ${parseError.message}`
          );
        }

        // Persist first save of synthetic rows even when still at defaults
        // so ops can enable flags without a prior Mongo seed.
        changedSettings.push({
          key,
          value: newValue,
          type: meta.type,
          scope: "private",
          category: meta.category,
          description: meta.description,
          metadata: meta.metadata,
        });
      });

      if (changedSettings.length === 0) {
        message.info("No changes to save");
        return;
      }

      const response = await setMultipleSettings(changedSettings);
      if (response.success) {
        message.success(`Successfully updated ${changedSettings.length} setting(s)`);
        await loadSettings();
        setHasChanges(false);
      } else {
        message.error(response.error || "Failed to save settings");
      }
    } catch (error) {
      message.error("Failed to save settings: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = () => {
    setHasChanges(true);
  };

  const renderSettingField = (setting) => {
    const { key, type, description, metadata = {} } = setting;

    if (key === "ops.safety.unsafeAreas") {
      return <UnsafeAreasSettingsPanel />;
    }

    if (metadata.control === "select" && Array.isArray(metadata.options)) {
      return (
        <Select
          {...commonProps}
          options={metadata.options.map((option) =>
            typeof option === "object"
              ? option
              : { label: String(option), value: option }
          )}
        />
      );
    }

    switch (type) {
      case "boolean":
        return <Switch />;

      case "number":
        return (
          <InputNumber
            style={{ width: "100%" }}
            min={metadata.min}
            max={metadata.max}
            step={metadata.step || 1}
            precision={metadata.precision}
            formatter={
              metadata.unit === "percent"
                ? (value) => `${value}%`
                : undefined
            }
            parser={
              metadata.unit === "percent"
                ? (value) => value.replace("%", "")
                : undefined
            }
          />
        );

      case "string":
        if (description && description.length > 100) {
          return <TextArea rows={3} />;
        }
        return (
          <Input
            type={key.toLowerCase().includes("email") ? "email" : "text"}
            placeholder={
              key === "leads.fulfilmentDiscovery.notificationEmail"
                ? "sales@bubakii.co.za"
                : key === "marketplace.experiment.startAt"
                  ? "2026-08-08T12:00:00.000Z"
                  : undefined
            }
          />
        );

      case "array":
      case "object":
        return (
          <TextArea
            rows={8}
            style={{ fontFamily: "monospace", fontSize: 12 }}
            placeholder={
              type === "array"
                ? "JSON array"
                : "JSON object"
            }
          />
        );

      default:
        return <Input />;
    }
  };

  const renderCategoryTab = (category, categorySettings) => {
    if (category === "pricing") {
      return <PricingSettingsPanel settings={categorySettings || []} />;
    }

    const visibleSettings = (categorySettings || []).filter(
      (setting) =>
        !isMarketplaceSettingKey(setting.key) &&
        !isLeadIntelligenceSettingKey(setting.key)
    );

    if (category === "features") {
      return (
        <>
          {marketplaceSettings.length > 0 && (
            <MarketplacePanelBlock>
              <MarketplaceSettingsPanel settings={marketplaceSettings} />
            </MarketplacePanelBlock>
          )}
          {visibleSettings.length === 0 && marketplaceSettings.length === 0 ? (
            <Alert
              message="No settings in this category"
              type="info"
              showIcon
            />
          ) : (
            visibleSettings.map((setting) => renderSettingRow(setting))
          )}
        </>
      );
    }

    if (category === "leads") {
      return (
        <>
          {leadIntelligenceSettings.length > 0 && (
            <LeadIntelligencePanelBlock>
              <LeadIntelligenceSettingsPanel
                settings={leadIntelligenceSettings}
              />
            </LeadIntelligencePanelBlock>
          )}
          {visibleSettings.length === 0 &&
          leadIntelligenceSettings.length === 0 ? (
            <Alert
              message="No settings in this category"
              type="info"
              showIcon
            />
          ) : (
            visibleSettings.map((setting) => renderSettingRow(setting))
          )}
        </>
      );
    }

    if (category === "quotes") {
      return (
        <>
          <QuotePolicyPanelBlock>
            <QuotePolicySettingsPanel />
          </QuotePolicyPanelBlock>
          {visibleSettings.length === 0 ? (
            <Alert
              message="No additional quote settings"
              type="info"
              showIcon
            />
          ) : (
            visibleSettings.map((setting) => renderSettingRow(setting))
          )}
        </>
      );
    }

    if (!categorySettings || categorySettings.length === 0) {
      return (
        <Alert
          message="No settings in this category"
          type="info"
          showIcon
        />
      );
    }

    if (visibleSettings.length === 0) {
      return (
        <Alert
          message="No settings in this category"
          type="info"
          showIcon
        />
      );
    }

    return <>{visibleSettings.map((setting) => renderSettingRow(setting))}</>;
  };

  const renderSettingRow = (setting) => {
    return (
      <SettingRow key={setting.key}>
        <SettingLabel>
          <Form.Item
            name={setting.key}
            valuePropName={setting.type === "boolean" ? "checked" : "value"}
            label={
              <span>
                {formatSettingLabel(setting.key)}
                {setting.scope === "public" ? (
                  <Tooltip title="This setting is visible to customers">
                    <UnlockOutlined
                      style={{ marginLeft: 8, color: "#52c41a" }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="This setting is admin-only">
                    <LockOutlined
                      style={{ marginLeft: 8, color: "#faad14" }}
                    />
                  </Tooltip>
                )}
              </span>
            }
            rules={[
              {
                required: false,
                message: `Please enter ${setting.key}`,
              },
            ]}
          >
            {renderSettingField(setting)}
          </Form.Item>
        </SettingLabel>
        {setting.description && setting.key !== "ops.safety.unsafeAreas" && (
          <SettingDescription>{setting.description}</SettingDescription>
        )}
        {setting.metadata?.unit && (
          <Tag color="blue" style={{ marginTop: 4 }}>
            Unit: {setting.metadata.unit}
          </Tag>
        )}
      </SettingRow>
    );
  };

  const categoryTabs = [
    {
      key: "business",
      label: (
        <span>
          <GlobalOutlined /> Business
        </span>
      ),
      icon: <GlobalOutlined />,
    },
    {
      key: "contact",
      label: (
        <span>
          <PhoneOutlined /> Contact
        </span>
      ),
      icon: <PhoneOutlined />,
    },
    {
      key: "payments",
      label: (
        <span>
          <BankOutlined /> Payments
        </span>
      ),
      icon: <BankOutlined />,
    },
    {
      key: "pricing",
      label: (
        <span>
          <DollarOutlined /> Pricing
        </span>
      ),
      icon: <DollarOutlined />,
    },
    {
      key: "quotes",
      label: "Quotes",
    },
    {
      key: "leads",
      label: (
        <span>
          <TeamOutlined /> Leads
        </span>
      ),
      icon: <TeamOutlined />,
    },
    {
      key: "tokens",
      label: "Tokens",
    },
    {
      key: "limits",
      label: "Limits",
    },
    {
      key: "features",
      label: (
        <span>
          <SettingOutlined /> Features
        </span>
      ),
      icon: <SettingOutlined />,
    },
    {
      key: "email",
      label: (
        <span>
          <MailOutlined /> Email
        </span>
      ),
      icon: <MailOutlined />,
    },
  ];

  if (loading) {
    return (
      <SettingsContainer>
        <Spin size="large" tip="Loading settings..." />
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={2}>System Settings</Title>
          <Text type="secondary">
            Configure application-wide settings. Changes take effect immediately.
          </Text>
        </div>

        <Alert
          type="info"
          showIcon
          icon={<HeartOutlined />}
          message="Integration health"
          description={
            <span>
              Verify Gmail, WhatsApp, Redis, and other credentials are configured
              and working.{" "}
              <Link to="/settings/system-health">Open system health →</Link>
            </span>
          }
        />

        {hasChanges && (
          <Alert
            message="You have unsaved changes"
            type="warning"
            showIcon
            action={
              <Button size="small" onClick={handleSave} loading={saving}>
                Save Now
              </Button>
            }
          />
        )}

        <Card
          extra={
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadSettings}
                disabled={loading}
              >
                Reload
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={!hasChanges}
              >
                Save All Changes
              </Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={categoryTabs
                .filter((tab) => {
                  if (tab.key === "pricing") return true;
                  if (tab.key === "features") {
                    return (
                      marketplaceSettings.length > 0 ||
                      (settingsByCategory.features?.length ?? 0) > 0
                    );
                  }
                  if (tab.key === "leads") {
                    return (
                      leadIntelligenceSettings.length > 0 ||
                      (settingsByCategory.leads?.length ?? 0) > 0
                    );
                  }
                  return (settingsByCategory[tab.key]?.length ?? 0) > 0;
                })
                .map((tab) => ({
                  key: tab.key,
                  label: tab.label,
                  forceRender: true,
                  children: renderCategoryTab(
                    tab.key,
                    settingsByCategory[tab.key] || []
                  ),
                }))}
            />
          </Form>
        </Card>
      </Space>
    </SettingsContainer>
  );
};

export default Settings;
